import type { ReactElement } from 'react'

import { FreshnessBadge } from '../../layout/FreshnessBadge'
import { trpc } from '../../trpc'
import { CodeLabel } from '../CodeLabel/CodeLabel'
import { ErrorState } from '../ErrorState/ErrorState'
import { Spinner } from '../Spinner/Spinner'
import styles from './productGroupPicker.module.scss'

export type ProductGroupPickerProps = {
  label: string
  /** The chosen groups' ids (`mrp.product_groups.group_id`), in the order they were chosen. */
  selected: Array<number>
  onChange: (groupIds: Array<number>) => void
  disabled?: boolean
}

/**
 * Picks several product groups of the mirror (`mrp.product_groups`, a few
 * dozen): one checkbox each, the whole label the touch target.
 *
 * ⚠️ A chosen id the mirror no longer has is listed first, checked, as
 * «desconocido» — never dropped: unchecking it is how somebody clears it, and
 * dropping it silently would remove it on the next save without anybody
 * having decided to.
 */
export const ProductGroupPicker = ({
  label,
  selected,
  onChange,
  disabled = false,
}: ProductGroupPickerProps): ReactElement => {
  const groupsQuery = trpc.configuration.listProductGroups.useQuery()

  const toggle = (groupId: number, checked: boolean): void => {
    onChange(checked ? [...selected, groupId] : selected.filter((id) => id !== groupId))
  }

  let body: ReactElement
  if (groupsQuery.isPending) {
    body = <Spinner label="Cargando grupos de producto…" />
  } else if (groupsQuery.isError) {
    body = <ErrorState error={groupsQuery.error} onRetry={() => void groupsQuery.refetch()} />
  } else {
    const groups = groupsQuery.data.data
    const known = new Set(groups.map((group) => group.groupId))
    const orphans = selected.filter((groupId) => !known.has(groupId))
    body = (
      <>
        <FreshnessBadge freshness={groupsQuery.data.freshness} />
        <ul className={styles['product-group-picker__list']}>
          {orphans.map((groupId) => (
            <li key={`orphan-${groupId}`}>
              <label className={styles['product-group-picker__option']}>
                <input
                  type="checkbox"
                  className={styles['product-group-picker__checkbox']}
                  checked
                  disabled={disabled}
                  onChange={(event) => toggle(groupId, event.target.checked)}
                />
                <CodeLabel code={`Grupo ${groupId}`} title={null} known={false} />
              </label>
            </li>
          ))}
          {groups.map((group) => (
            <li key={group.groupId}>
              <label className={styles['product-group-picker__option']}>
                <input
                  type="checkbox"
                  className={styles['product-group-picker__checkbox']}
                  checked={selected.includes(group.groupId)}
                  disabled={disabled}
                  onChange={(event) => toggle(group.groupId, event.target.checked)}
                />
                <CodeLabel code={group.code ?? `Grupo ${group.groupId}`} title={group.title} known />
              </label>
            </li>
          ))}
        </ul>
      </>
    )
  }

  return (
    <fieldset className={styles['product-group-picker']}>
      <legend className={styles['product-group-picker__legend']}>{label}</legend>
      {body}
    </fieldset>
  )
}
